/**
 * User Management Dashboard
 *
 * Comprehensive user management system with tabs for:
 * - Users
 * - Roles
 * - Groups
 * - Departments
 * - Designations
 *
 * This provides a single entry point for all user-related configuration
 */

import React, { useState, useEffect } from 'react';
import { Tabs, Card, Row, Col, Typography } from 'antd';
import {
  UserOutlined,
  SafetyOutlined,
  TeamOutlined,
  CloudServerOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import UserConfig2 from './UserConfig2';
import RoleManagement from './RoleManagement';
import GroupManagement from './GroupManagement';
import ActiveDirectoryIntegration from './ActiveDirectoryIntegration';
import UserAccessManagement from './UserAccessManagement';
import StatCard from '@/components/StatCard';
import { Users, UserCheck, Shield, Award } from 'lucide-react';
import { get_employee_user_list, get_roles, get_departments } from '@/utils/api';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';

const { Title } = Typography;

const UserManagementDashboard = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [activeTab, setActiveTab] = useState('users');
  const [employees_list, set_employees_list] = useState([]);
  const [roles_list, set_roles_list] = useState([]);
  const [departments_list, set_departments_list] = useState([]);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await get_employee_user_list((res) => {
          set_employees_list(res?.data || []);
        });
        await get_roles((res) => {
          set_roles_list(res?.data?.data || []);
        });
        await get_departments((res) => {
          set_departments_list(res?.data?.data || []);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div
      className="p-2.5 pt-5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        // minHeight (not a fixed height + overflow) — each tab's own table already
        // reserves its own bounded scroll.y footprint (~calc(100vh - 595px)), so
        // total content height per tab is predictable. Forcing a second, separate
        // fixed-height+scroll region on this outer Card on top of that left a large
        // dead gap below short tabs' content (their own table didn't fill this
        // Card's independently-computed body height).
        style={{ minHeight: 'calc(100vh - 120px)', backgroundColor: cardBgColor }}
        title={
          <div className="flex items-center gap-2">
            <Icons.Users className="w-7 h-7 text-blue-600" />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('User Management')}
            </Title>
          </div>
        }
      >
        {/* Top Summary Cards - Above Tabs */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <StatCard
              label="Total Users"
              value={employees_list?.length || 0}
              icon={<Users size={24} />}
              bgColor={isDark ? '#1e3a5f' : '#dbeafe'}
              iconColor={isDark ? '#60a5fa' : '#3b82f6'}
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Active Users"
              value={employees_list?.filter((emp) => emp?.is_active)?.length || 0}
              icon={<UserCheck size={24} />}
              bgColor={isDark ? '#14532d' : '#d1fae5'}
              iconColor={isDark ? '#4ade80' : '#10b981'}
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Total Roles"
              value={roles_list?.length || 0}
              icon={<Shield size={24} />}
              bgColor={isDark ? '#4c1d95' : '#f3e8ff'}
              iconColor={isDark ? '#c084fc' : '#a855f7'}
            />
          </Col>
          <Col span={6}>
            <StatCard
              label="Departments"
              value={departments_list?.length || 0}
              icon={<Award size={24} />}
              bgColor={isDark ? '#713f12' : '#fef9c3'}
              iconColor={isDark ? '#fbbf24' : '#facc15'}
            />
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={
              <span>
                <UserOutlined style={{ marginRight: 8 }} />
                Users
              </span>
            }
            key="users"
          >
            <UserConfig2 isEmbedded />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <SafetyOutlined style={{ marginRight: 8 }} />
                Roles & Permissions
              </span>
            }
            key="roles"
          >
            <RoleManagement />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                Groups
              </span>
            }
            key="groups"
          >
            <GroupManagement />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <CloudServerOutlined style={{ marginRight: 8 }} />
                Active Directory
              </span>
            }
            key="ad"
          >
            <ActiveDirectoryIntegration />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <KeyOutlined style={{ marginRight: 8 }} />
                User Access Management
              </span>
            }
            key="access"
          >
            <UserAccessManagement />
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default UserManagementDashboard;
